import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import OpenFilterLink from '../../components/OpenFilterLink';
import FilterSelect, { useFilterCache } from '../../components/FilterSelect';
import {
  Chip, FINDING_TONE, ScoreRing, coverageChip, healthColor, testsChip, useDhfBuild, utc,
} from './dhfUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';

const PAGE_SIZES = [25, 50, 100, 200];
const PAGE_SIZE_KEY = 'dhf.gridPageSize';

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'gap', label: 'Coverage gaps' },
  { id: 'failed', label: 'Test failed' },
  { id: 'not_done', label: 'Not complete' },
  { id: 'not_verified', label: 'Not verified' },
];

function Warn({ children }) {
  return (
    <div className="dhf-warn" role="alert">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" aria-hidden="true">
        <path d="M12 3 2 21h20L12 3z" /><path d="M12 10v5M12 18v.5" />
      </svg>
      <div>{children}</div>
    </div>
  );
}

/**
 * The Documents filter: a saved DOC filter whose rows map each document's ORIGINAL id (what
 * `Root Document Id` holds) to its ID in each version — a branched document has another ID per
 * version, and its outline can only be read by that one (Critical Note #118).
 */
function DocsFilter({ setupId, panel, orcanosUrl, onSaved }) {
  const { filterCache: cache, ensureFilters: ensure } = useFilterCache();
  const saved = panel.docs_filter_id || '';
  const [value, setValue] = useState(saved);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(saved); }, [saved]);

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await dhfApi.saveSettings(setupId, { docs_filter_id: String(value || '') });
      setMsg('Saved — Refresh to read the documents with it.');
      onSaved();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dhf-docs-filter">
      <label>Documents filter</label>
      {panel.can_manage ? (
        <>
          <div className="dhf-docs-filter-pick">
            <FilterSelect projectId={panel.project_id} itemTypeCode="DOC" itemTypeLabel="Document"
                          versionId={panel.version_id} orcanosUrl={orcanosUrl} value={value}
                          onChange={setValue} cache={cache} ensure={ensure} placeholder="DOC filter id" />
          </div>
          {String(value || '') !== String(saved) && (
            <button type="button" className="drv-btn drv-btn--primary" disabled={busy} onClick={save}>Save</button>
          )}
        </>
      ) : (
        <span>{saved ? `#${saved}` : 'Not set'}</span>
      )}
      <span className="drv-info">
        <button type="button" className="drv-info-btn" aria-label="About the Documents filter">i</button>
        <span className="drv-info-pop" role="tooltip">
          A filter of <strong>Document</strong> items. It finds each document’s ID in the panel’s
          versions — a branched document has another ID there.
        </span>
      </span>
      {msg && <span className="drv-muted dhf-docs-filter-msg">{msg}</span>}
    </div>
  );
}

/**
 * One DHF (mockup screen 1): the panel's documents with a health score each, the selected
 * document's score parts, and its items in document order with coverage and verification.
 * The selected document is `?doc=` so it deep-links.
 */
export default function DhfPage() {
  const { setupId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [grid, setGrid] = useState(null);
  const [finding, setFinding] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [warnOpen, setWarnOpen] = useState(false);
  const [pageSize, setPageSize] = useState(() => {
    try { return Number(localStorage.getItem(PAGE_SIZE_KEY)) || 50; } catch { return 50; }
  });

  const load = useCallback(async () => {
    try {
      setData(await dhfApi.get(setupId));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId]);
  useEffect(() => { load(); }, [load]);
  const build = useDhfBuild(setupId, load);

  const docs = useMemo(() => data?.docs || [], [data]);
  const docId = params.get('doc') || docs[0]?.id || '';
  const doc = docs.find((d) => d.id === docId) || null;

  useEffect(() => {
    if (!docId || !data?.built) return undefined;
    let live = true;
    dhfApi.items(setupId, { doc_id: docId, finding, q, page, page_size: pageSize })
      .then((g) => { if (live) setGrid(g); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [setupId, docId, finding, q, page, pageSize, data]);

  const changePageSize = (n) => {
    try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* per-browser convenience only */ }
    setPageSize(n);
    setPage(1);
  };

  const byLevel = useMemo(() => {
    const out = [];
    docs.forEach((d) => {
      const name = d.levels[0] || '';
      let g = out.find((x) => x.name === name);
      if (!g) { g = { name, docs: [] }; out.push(g); }
      g.docs.push(d);
    });
    return out;
  }, [docs]);

  const pick = (id) => { setParams({ doc: id }); setFinding(''); setPage(1); };
  const levelName = (i) => (data?.levels || []).find((l) => String(l.i) === String(i))?.name || `Level ${Number(i) + 1}`;
  const poolTotal = Object.values(data?.pool || {}).reduce((a, b) => a + b, 0);

  const buildBar = (
    <div className="dhf-build">
      {build.running ? (
        <>
          <div className="dhf-progress" aria-label="Build progress"><i style={{ width: `${build.progress}%` }} /></div>
          <span>{build.message}</span>
        </>
      ) : (
        <>
          <div className="dhf-build-btns">
            {data?.panel && (
              <Link className="drv-btn" to={`/trace/build/${setupId}/import?from=dhf`}
                    title="AI Studio: bring customer documents into this panel as items and traces — Create can place them in a DHF document">
                Import</Link>
            )}
            {data?.built && (
              <Link className="drv-btn" to={`/dhf/${setupId}/baselines`}
                    title="Freeze the DHF as it is now, and compare it later">Baselines</Link>
            )}
            <button type="button" className="drv-btn drv-btn--primary" onClick={() => build.start(true)}
                    title="Re-read what changed in Orcanos, then re-score every document">
              {data?.built ? 'Refresh' : 'Build DHF'}
            </button>
          </div>
          {data?.built_at && <span className="dhf-build-at">Built {utc(data.built_at)}</span>}
        </>
      )}
    </div>
  );

  return (
    <div className="drv-page dhf-page">
      <div className="drv-head">
        <div>
          <h1 className="drv-title">{data?.panel?.name || 'DHF'}</h1>
          {data?.panel && (
            <div className="drv-muted" style={{ fontSize: 12 }}>
              From the Traceability panel · <Link to={`/trace/matrix/${setupId}`}>open the matrix</Link>
            </div>
          )}
        </div>
        {buildBar}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      {build.error && <div className="drv-error" role="alert">{build.error}</div>}
      {data === null && !error && <div className="drv-muted">Loading…</div>}
      {data && !data.built && (
        <div className="drv-empty">
          This DHF has not been built yet. <strong>Build DHF</strong> reads the panel, the status tables and
          every document's outline from Orcanos — it can take a few minutes on a large panel.
        </div>
      )}

      {data?.panel?.linked && (
        <DocsFilter setupId={setupId} panel={data.panel} orcanosUrl={data.orcanos_url} onSaved={load} />
      )}

      {(data?.warnings || []).length > 0 && (
        <div className="dhf-warn-group">
          <button type="button" className="dhf-warn-sum" aria-expanded={warnOpen}
                  onClick={() => setWarnOpen((o) => !o)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" aria-hidden="true">
              <path d="M12 3 2 21h20L12 3z" /><path d="M12 10v5M12 18v.5" />
            </svg>
            <span>
              {data.built ? 'Succeeded with ' : ''}
              {data.warnings.length} {data.warnings.length === 1 ? 'error' : 'errors'}
            </span>
            <span className="dhf-warn-toggle">{warnOpen ? 'Hide ▴' : 'Show ▾'}</span>
          </button>
          {warnOpen && data.warnings.map((w, i) => (
            <Warn key={i}>
              {w.text}
              {(w.filters || []).map((f) => (
                <span key={f.filter_id} className="dhf-warn-link">
                  <OpenFilterLink baseUrl={data.orcanos_url} versionId={f.version_id} itemTypeCode={f.type}
                                  filterId={f.filter_id} label={`filter ${f.filter_name || f.filter_id}`} />
                  {w.filters.length > 1 && <span className="drv-muted"> {f.filter_name || f.filter_id}</span>}
                </span>
              ))}
            </Warn>
          ))}
        </div>
      )}

      {data?.built && docs.length === 0 && (
        <div className="drv-empty">None of this panel's items is in a document (every Root Document Id is empty or not returned).</div>
      )}

      {docs.length > 0 && (
        <div className="dhf-layout">
          <section className="drv-card dhf-docs" aria-label="Documents">
            <div className="dhf-docs-h">
              <h2 className="drv-h2">Documents</h2>
              <span>{docs.length} · {docs.reduce((a, d) => a + d.n_items, 0)} items</span>
            </div>
            {byLevel.map((g) => (
              <div key={g.name}>
                <div className="dhf-lv">{g.name}</div>
                {g.docs.map((d) => (
                  <button key={d.id} type="button" className={`dhf-docbtn${d.id === docId ? ' dhf-docbtn--on' : ''}`}
                          onClick={() => pick(d.id)} aria-pressed={d.id === docId}>
                    <ScoreRing health={d.score?.health} />
                    <span className="dhf-docbtn-t">
                      <b>{d.name}</b>
                      <span>{d.key} · {d.n_items} items</span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
            {poolTotal > 0 && (
              <div className="dhf-pool-box">
                <strong>Pool — in no document</strong><br />
                {Object.entries(data.pool).filter(([, n]) => n).map(([i, n]) => `${n} ${levelName(i)}`).join(' · ')}.
                {' '}Open a document to place them under a paragraph.
              </div>
            )}
          </section>

          {doc && (
            <section className="dhf-main" aria-label="Selected document">
              <div className="drv-card dhf-score">
                <ScoreRing health={doc.score?.health} size={104} />
                <div className="dhf-score-t">
                  <div className="dhf-eyebrow">{doc.levels.join(' · ')}</div>
                  <h2>{doc.name}</h2>
                  <div className="dhf-score-sub">
                    {doc.url ? <a className="dhf-key" href={doc.url} target="_blank" rel="noopener noreferrer">{doc.key} ↗</a> : doc.key}
                    {' '}· {doc.n_items} items{doc.outline_ok ? ` in ${doc.paragraphs} paragraphs` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="drv-btn drv-btn--primary"
                            onClick={() => navigate(`/dhf/${setupId}/doc/${doc.id}`)}>Open document</button>
                    {doc.url && (
                      <a className="drv-btn" href={doc.url} target="_blank" rel="noopener noreferrer"
                         title="Open this document in Orcanos (new tab)">Open in Orcanos ↗</a>
                    )}
                  </div>
                </div>
                <div className="dhf-parts">
                  {(doc.score?.parts || []).map((p) => {
                    const h = p.available ? Math.round(100 * (1 - p.share)) : null;
                    return (
                      <div key={p.key} className={p.available ? '' : 'dhf-part--off'}>
                        <div className="dhf-part-h">
                          <span><b>{p.label}</b> <span>· {p.weight}%</span></span>
                          <b>{p.available ? `${h}%` : 'not scored'}</b>
                        </div>
                        <div className="dhf-bar"><i style={{ width: `${h || 0}%`, background: healthColor(h) }} /></div>
                        <div className="dhf-part-hint">{p.available ? `${p.hint} · ${p.n} counted` : p.hint}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="drv-card" style={{ overflow: 'hidden' }}>
                <div className="dhf-grid-head">
                  <h3>Items in this document</h3>
                  <div className="dhf-filters" role="group" aria-label="Show">
                    {FILTERS.map((f) => (
                      <button key={f.id || 'all'} type="button"
                              className={`dhf-fbtn${finding === f.id ? ' dhf-fbtn--on' : ''}`}
                              onClick={() => { setFinding(f.id); setPage(1); }}>
                        {f.label}{f.id ? ` · ${doc.findings?.[f.id] || 0}` : ` · ${doc.n_items}`}
                      </button>
                    ))}
                  </div>
                  <input className="dhf-search" style={{ width: 200, margin: 0 }} type="search" placeholder="Search key or name…"
                         aria-label="Search items" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
                </div>
                <div className="dhf-table-wrap">
                  <table className="dhf-table">
                    <thead>
                      <tr>
                        <th>§</th><th>Key</th><th>Item</th><th>Status</th><th>Coverage</th><th>Verification</th><th>Finding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(grid?.rows || []).map((r) => (
                        <tr key={r.uid}>
                          <td className="dhf-sec">{r.section || '—'}</td>
                          <td>{r.url ? <a className="dhf-key" href={r.url} target="_blank" rel="noopener noreferrer">{r.key}</a> : r.key}</td>
                          <td><div>{r.name}</div><div className="dhf-sub">{r.paragraph || r.level}</div></td>
                          <td>
                            {r.status ? (
                              <Chip tone={r.done === true ? 'good' : r.done === false ? 'warn' : 'neutral'}>
                                {r.status}{r.done === true ? ' · done' : ''}
                              </Chip>
                            ) : '—'}
                          </td>
                          <td>{coverageChip(r) || <span className="dhf-sub">—</span>}</td>
                          <td>{testsChip(r.tests) || <span className="dhf-sub">—</span>}</td>
                          <td><Chip tone={FINDING_TONE[r.finding]}>{data.finding_labels?.[r.finding] || r.finding}</Chip></td>
                        </tr>
                      ))}
                      {grid && grid.rows.length === 0 && (
                        <tr><td colSpan={7} className="drv-muted">Nothing to show.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {grid && grid.total > 0 && (
                  <div className="drv-pager dhf-pager">
                    <span>
                      {(grid.page - 1) * grid.page_size + 1}–{Math.min(grid.page * grid.page_size, grid.total)} of {grid.total}
                    </span>
                    <div className="dhf-pager-nav">
                      <select className="drv-input dhf-pager-size" value={pageSize} aria-label="Rows per page"
                              onChange={(e) => changePageSize(Number(e.target.value))}>
                        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} rows</option>)}
                      </select>
                      <button type="button" className="drv-btn" disabled={grid.page <= 1} onClick={() => setPage(1)}
                              aria-label="First page">«</button>
                      <button type="button" className="drv-btn" disabled={grid.page <= 1} onClick={() => setPage(grid.page - 1)}>‹ Previous</button>
                      <span>Page {grid.page} of {grid.pages}</span>
                      <button type="button" className="drv-btn" disabled={grid.page >= grid.pages} onClick={() => setPage(grid.page + 1)}>Next ›</button>
                      <button type="button" className="drv-btn" disabled={grid.page >= grid.pages} onClick={() => setPage(grid.pages)}
                              aria-label="Last page">»</button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
